import React, { useCallback, useMemo, useState } from 'react'
import { Grid, GridItem, Text, Toggle } from '@/components'
import { AlertMessage, NavigationPage } from '@/app/components'
import connectUtilityAnimation from '@/app/img/connect-utility-lottie.json'
import { AdaptiveCardRowLayout } from '@/app/layouts'
import Lottie from 'lottie-react'
import { Thermostat, ThermostatSettings } from '@/types'
import { debounce } from '@/utils/limit'
import { useAppDispatch } from '@/hooks'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { thermostatActiveSettingsCollection } from '@/reducers/thermostatActiveSettings'
import { thermostatsCollection } from '@/reducers/thermostats'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import { useParams } from 'react-router-dom'
import { useBrandedThermostatName } from '@/hooks/useBrandedThermostatName'
import EstimatedAnnualSavingsRow from 'features/devices/thermostat/EstimatedAnnualSavingsRow'
import { thermostatPrecoolSavingsSummariesCollection } from '@/reducers/thermostatSavingsSummaries'
import { plansCollection } from '@/reducers/plansCollection'

type ViewModelProps = {
  settings: ThermostatSettings | undefined
  thermostatId: Thermostat['id']
  isEcobee: boolean
}

function useViewModel(props: ViewModelProps) {
  const dispatch = useAppDispatch()

  const {
    data: savingsSummary,
    status: savingsSummaryStatus,
    error: savingsError,
    refetch: refetchSavingsSummary,
  } = thermostatPrecoolSavingsSummariesCollection.useFetch(
    `/thermostats/${props.thermostatId}/savings_summary/pre_cool/`,
  )

  const [localPrecoolEnabled, setLocalPrecoolEnabled] = useState(
    props.isEcobee ? false : props.settings?.pre_cool_enabled,
  )

  const debouncedUpdateSettings = useCallback(
    debounce((updatedSettings: Partial<ThermostatSettings>) => {
      dispatch(
        thermostatSettingsCollection.actions.update(
          `/thermostats/${props.thermostatId}/settings/`,
          updatedSettings,
          { id: props.settings?.id },
        ),
      )
      dispatch(thermostatActiveSettingsCollection.actions.invalidate())
    }, 500),
    [props.thermostatId, props.settings?.id],
  )

  const brandedThermostatName = useBrandedThermostatName(props.thermostatId)

  const onPrecoolToggle = (checked: boolean) => {
    if (props.isEcobee) return
    setLocalPrecoolEnabled(checked)
    debouncedUpdateSettings({ pre_cool_enabled: checked })
  }

  const { data: plans } = plansCollection.useFetch()
  const isTOUPlan = useMemo(() => plans.some((p) => p.is_time_of_use), [plans])

  return {
    brandedThermostatName,
    onPrecoolToggle,
    localPrecoolEnabled,
    savingsCents: savingsSummary?.[0]?.pre_cool_savings_cents,
    savingsSummary,
    savingsSummaryStatus,
    savingsError,
    refetchSavingsSummary,
    isTOUPlan,
    isEcobee: props.isEcobee,
  }
}

function PrecoolPreheatSettingsPageComponent(
  props: ReturnType<typeof useViewModel>,
) {
  return (
    <NavigationPage
      id="thermostat-precool-preheat-settings-page"
      title="Pre-cool"
      subtitle={props.brandedThermostatName ?? 'Thermostat'}
      navigationOptions={['back']}
    >
      <AdaptiveCardRowLayout.PlainRow>
        <GridItem placeItems="center">
          <Lottie
            className="w-1/2"
            animationData={connectUtilityAnimation}
            loop
          />
        </GridItem>
      </AdaptiveCardRowLayout.PlainRow>
      {props.isEcobee ? (
        <AlertMessage variant="warning" className="my-2">
          Pre-cool is currently unavailable for your thermostat.
        </AlertMessage>
      ) : (
        !props.isTOUPlan && (
          <AlertMessage variant="info" className="my-2">
            Precool is only available for users on a time-of-use utility plan
          </AlertMessage>
        )
      )}
      <AdaptiveCardRowLayout.CardRow bottomRule={!props.isEcobee}>
        <Grid flow="column">
          <GridItem>
            <Text variant="subheader">Pre-cool</Text>
            <Text variant="body2">
              Optiwatt will cool your home down to your desired temperature when
              electricity rates are cheapest
            </Text>
          </GridItem>
          <GridItem className="pt-2 pl-16">
            <Toggle
              id="thermostat-precool-toggle"
              checked={props.isEcobee ? false : props.localPrecoolEnabled}
              onChange={props.onPrecoolToggle}
              disabled={props.isEcobee}
            />
          </GridItem>
        </Grid>
      </AdaptiveCardRowLayout.CardRow>
      {!props.isEcobee && (
        <AdaptiveCardRowLayout.CardRow bottomRule={false}>
          <EstimatedAnnualSavingsRow
            error={Boolean(props.savingsError)}
            loading={props.savingsSummaryStatus === 'loading'}
            savingsCents={props.savingsCents}
          />
        </AdaptiveCardRowLayout.CardRow>
      )}
    </NavigationPage>
  )
}

function PrecoolPreheatSettingsPage(props: ViewModelProps) {
  const vm = useViewModel(props)
  return <PrecoolPreheatSettingsPageComponent {...vm} />
}

function PrecoolPreheatSettingsPageWithFallback(props: {
  thermostatId: Thermostat['id']
}) {
  const { data: settingsArray, status: settingsStatus } =
    thermostatSettingsCollection.useFetch(
      `/thermostats/${props.thermostatId}/settings/`,
    )
  const settings = settingsArray?.[0]
  const settingsLoading = settingsStatus !== 'succeeded' && !settings

  const { data: thermostats } = thermostatsCollection.useFetch()
  const thermostat = thermostats.find((t) => t.id === props.thermostatId)
  const isEcobee = thermostat?.portal === 'Ecobee'

  if (settingsLoading) {
    return <PageLoader />
  }

  return (
    <PrecoolPreheatSettingsPage
      thermostatId={props.thermostatId}
      settings={settings}
      isEcobee={isEcobee}
    />
  )
}

type PathParams = { deviceId: string | undefined }

export default function PrecoolPreheatSettingsPageWithParams() {
  const { deviceId } = useParams<PathParams>()
  if (!deviceId) return <PageLoader />
  const thermostatId = parseInt(deviceId)
  if (isNaN(thermostatId)) return <PageLoader />
  return <PrecoolPreheatSettingsPageWithFallback thermostatId={thermostatId} />
}
